import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { LanguageModel } from '../../models/languages.model';
import { MessageContent } from '../../models/message-content.model';

import useGetFormulaMessageContents from '../formulas/useGetFormulaMessageContent';
import useHandleQueryParams from '../helper/useHandleQueryParams';
import useLanguagesQuery from '../languages/useLanguagesQuery';

// A formula's default-language content can be genuinely missing -- e.g. a
// v2-created formula authored under a real, non-default language for this
// account, or any formula whose account-default language changed after
// creation. Falling back to the lowest display_order content (the same one
// the formula's own `structure` field already resolves to server-side, via
// GET /formulas/{id}/message-contents -> MessageContentService.list's
// order_by=[display_order.asc()]) means every consumer of
// currentMessageContentId still resolves to something, instead of silently
// staying unresolved forever with no error and no loading state.
//
// `languages` takes the whole array (not a pre-resolved default id) so
// "still loading" (undefined) is distinguishable from "loaded, no default
// language" (an array with no is_default match) -- useLanguagesQuery and
// useGetFormulaMessageContents fire in parallel, and messageContents commonly
// resolves first. Falling back before languages has loaded would hand a
// concrete (and possibly wrong) message_content_id to every mutation hook
// that reads currentMessageContentId (variant/button/label create-update-
// delete), for a formula that DOES have real default-language content once
// languages finishes loading.
export function resolveDefaultMessageContentId(
    messageContents: MessageContent[] | undefined,
    languages: LanguageModel[] | undefined,
): string | undefined {
    if (languages === undefined) {
        return undefined;
    }
    const defaultLanguageId = languages.find(
        (language) => language.is_default,
    )?.id;
    const defaultLanguageMatch = messageContents?.find(
        (content) => content.language_id === defaultLanguageId,
    );
    return defaultLanguageMatch?.id ?? messageContents?.[0]?.id;
}

export default function useGetCurrentMessageContent(formulaId?: number): {
    currentMessageContentId?: string;
    isLoading: boolean;
} {
    const { formulaId: formulaIdParam } = useParams<{ formulaId: string }>();
    const [queryParams] = useHandleQueryParams();

    const messageContentId = useMemo<string | undefined>(
        () => queryParams.messageContentId as string | undefined,
        [queryParams],
    );

    const { data: messageContents, isLoading } = useGetFormulaMessageContents(
        formulaId || +formulaIdParam!,
    );
    const { data: languages } = useLanguagesQuery();

    const defaultMessageContentId = useMemo<string | undefined>(
        () => resolveDefaultMessageContentId(messageContents, languages),
        [messageContents, languages],
    );

    const currentMessageContentId = useMemo<string | undefined>(
        () => messageContentId || defaultMessageContentId,
        [messageContentId, defaultMessageContentId],
    );

    return { currentMessageContentId, isLoading };
}
